import { useAssertedTenancyContext } from "@bfc-software/app-toolbox";
import { useTaskPlannerApi } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";
import type { QueryLike } from "@bfc-software/bfc-web-ui/lib/async-utils";

import { ActivationFilter } from "../../tasks/-data/filters";
import { activationQuery } from "../../tasks/-data/use-plans";
import { useShifts } from "../../system-controls/-data/warehouse-settings-client";
import { DASHBOARD_SAMPLE } from "./dashboard-mock";
import {
  currentShift,
  dayStartedAt,
  distinctRoutes,
  shiftStartedAt,
  pickerCounts,
  pickersDetail,
  notActivated,
  taskProgress,
  type Dashboard,
  zoneProgress,
  type TaskCounts,
} from "./dashboard-model";

type ActivationStatus = "pending" | "assigned" | "claimed";

/** How often the live figures are re-read. */
const REFRESH_MS = 30_000;
const POLL = { refetchInterval: REFRESH_MS };

/** The listing's page cap; the most rows one read can see. */
const PAGE_LIMIT = 100;

/** A page of rows (not a count) for the given statuses. */
const pageInit = (
  orgId: string,
  warehouseId: string,
  status: ActivationStatus[],
) => ({
  params: {
    path: { orgId },
    query: { warehouseExtId: warehouseId, status, limit: PAGE_LIMIT },
  },
});

/**
 * The dashboard's data: live task counts, routes, pickers and the running shift
 * (from the warehouse's system controls), plus
 * `DASHBOARD_SAMPLE` for what has no endpoint yet. Everything live is re-read
 * every `REFRESH_MS`.
 *
 * Task counts, unit totals and pieces per hour come from one read of the stats endpoint, scoped
 * to the warehouse's dashboard population: unfinished work whenever it was
 * activated, completed work only since the shift began (or midnight, with no
 * shift running), so a shift's completion starts from zero.
 *
 * Routes are read from one page of the unfinished activations, so a warehouse
 * with more than a page of them gets a floor (`capped`), not a count. Pickers
 * are the open selector sessions, split by whether the selector holds a claimed
 * task; claimed tasks are bounded by the number of selectors, so one page of them
 * is enough.
 *
 * Org and warehouse come from `useAssertedTenancyContext()`, so this only runs
 * below the warehouse gate.
 */
export function useDashboard(): QueryLike<Dashboard> {
  const { orgId, warehouseId } = useAssertedTenancyContext();
  const client = useTaskPlannerApi();
  const path = "/{orgId}/task-activations" as const;

  const unfinished = client.useQuery(
    "get",
    path,
    pageInit(orgId, warehouseId, ["pending", "assigned", "claimed"]),
    POLL,
  );
  const claimedRows = client.useQuery(
    "get",
    path,
    pageInit(orgId, warehouseId, ["claimed"]),
    POLL,
  );
  // The plans the Tasks tab's "Awaiting activation" chip lists, so the card and
  // the page it opens agree about which plans these are.
  const plans = client.useQuery(
    "get",
    "/{orgId}/task-plans",
    {
      params: {
        path: { orgId },
        query: {
          warehouseExtId: warehouseId,
          limit: PAGE_LIMIT,
          ...activationQuery(ActivationFilter.NOT_ACTIVATED),
        },
      },
    },
    POLL,
  );
  const sessions = client.useQuery(
    "get",
    "/{orgId}/selector-sessions",
    {
      params: {
        path: { orgId },
        query: { warehouseExtId: warehouseId, open: true, limit: PAGE_LIMIT },
      },
    },
    POLL,
  );

  // A failed shifts read leaves the header's shift blank and counts completions
  // from the start of the day, rather than failing the dashboard; the loading
  // wait still covers it.
  const shifts = useShifts(orgId, warehouseId);
  const timezone = shifts.data?.warehouseTimezone ?? "unknown";
  const now = Date.now();
  const shift = shifts.data
    ? currentShift(shifts.data.shifts, timezone, now)
    : undefined;
  // Whole minutes, so this is the same value for the whole shift and keys the
  // completed read without re-keying it every render.
  const since =
    (shift ? shiftStartedAt(shift, timezone, now) : undefined) ??
    dayStartedAt(timezone, now);

  // The stats read waits for the shift so it is keyed once, on the real window.
  const stats = client.useQuery(
    "get",
    "/{orgId}/task-activation-stats",
    {
      params: {
        path: { orgId },
        query: {
          warehouseExtId: warehouseId,
          completedSince: new Date(since).toISOString(),
          // Hour boundaries are the warehouse's; without its zone the service
          // leaves `hourly` out.
          ...(shifts.data?.warehouseTimezone
            ? { timezone: shifts.data.warehouseTimezone }
            : {}),
        },
      },
    },
    { ...POLL, enabled: !shifts.isPending },
  );

  const failed = [stats, plans, unfinished, claimedRows, sessions].find(
    (r) => r.isError,
  );
  if (failed) {
    return {
      isPending: false,
      isError: true,
      error: failed.error,
      data: undefined,
    };
  }
  if (
    stats.data === undefined ||
    plans.data === undefined ||
    unfinished.data === undefined ||
    claimedRows.data === undefined ||
    sessions.data === undefined ||
    shifts.isPending
  ) {
    return { isPending: true, isError: false, error: null, data: undefined };
  }

  const counts: TaskCounts = stats.data.tasks;
  // Lines that came up short will never be picked, so they leave the total:
  // a finished task with a short pick reads as done, not as a standing gap.
  const { planned, picked, shorted } = stats.data.units;

  return {
    isPending: false,
    isError: false,
    error: null,
    data: {
      tasks: taskProgress(counts),
      units: { picked, total: planned - shorted },
      notActivated: notActivated(
        plans.data.task_plans,
        plans.data.pagination.has_more,
      ),
      zones: zoneProgress(stats.data.zones),
      hourly: (stats.data.hourly ?? []).map(({ hour, pieces, partial }) => ({
        hour,
        pieces,
        partial,
      })),
      since,
      activatedRoutes: distinctRoutes(
        unfinished.data.task_activations,
        PAGE_LIMIT,
      ),
      pickers: pickerCounts(sessions.data.sessions, now),
      pickersDetail: pickersDetail(
        sessions.data.sessions,
        claimedRows.data.task_activations,
        now,
      ),
      shift,
      updatedAt: Math.min(
        stats.dataUpdatedAt,
        plans.dataUpdatedAt,
        unfinished.dataUpdatedAt,
        claimedRows.dataUpdatedAt,
        sessions.dataUpdatedAt,
      ),
      sample: DASHBOARD_SAMPLE,
    },
  };
}
